import { useEffect, useState } from "react";
import { api, SupportButton } from "../lib/api";
import { ROUTE } from "../lib/paths";

const ACTIONS: { value: SupportButton["action"]; label: string }[] = [
  { value: "pay", label: "Ссылка на оплату" },
  { value: "ceo", label: "Чат CEO бота" },
  { value: "main", label: "Чат CEO UralBass" },
  { value: "ubass_form", label: "Форма UBass" },
  { value: "link", label: "Ссылка (URL)" },
  { value: "text", label: "Текст" },
];

const payloadPlaceholder = (action: SupportButton["action"]) => {
  if (action === "link") return "https://...";
  if (action === "text") return "Текст сообщения";
  if (action === "ceo" || action === "main") return "@username (опционально)";
  if (action === "ubass_form") return "URL формы (опционально)";
  return "Опционально";
};

export default function Settings() {
  const [startMsg, setStartMsg] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [buttons, setButtons] = useState<SupportButton[]>([]);
  const [buttonsLoading, setButtonsLoading] = useState(false);
  const [buttonsNotice, setButtonsNotice] = useState<string | null>(null);
  const [newBtn, setNewBtn] = useState<Partial<SupportButton>>({
    label: "",
    action: "link",
    payload: "",
    enabled: true,
    sort_order: 100,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const r = await api.adminSettings.get("start_message");
        if (!alive) return;
        setStartMsg(r.item?.value || "");
      } catch {
        if (alive) setStartMsg("");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setButtonsLoading(true);
      try {
        const r = await api.adminSupportButtons.list();
        if (!alive) return;
        setButtons(r.items || []);
      } catch {
        if (alive) setButtons([]);
      } finally {
        if (alive) setButtonsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setNotice(null);
    try {
      await api.adminSettings.set("start_message", startMsg);
      setNotice("Сохранено");
      setTimeout(() => setNotice(null), 1800);
    } catch (e: any) {
      setNotice(e?.message || "Ошибка сохранения");
      setTimeout(() => setNotice(null), 2400);
    } finally {
      setSaving(false);
    }
  };

  const refreshButtons = async () => {
    setButtonsLoading(true);
    try {
      const r = await api.adminSupportButtons.list();
      setButtons(r.items || []);
    } catch (e: any) {
      setButtonsNotice(e?.message || "Ошибка загрузки");
      setTimeout(() => setButtonsNotice(null), 2400);
    } finally {
      setButtonsLoading(false);
    }
  };

  const updateButton = (id: number, patch: Partial<SupportButton>) => {
    setButtons((prev) =>
      prev.map((b) => (b.id === id ? { ...b, ...patch } : b))
    );
  };

  const saveButton = async (btn: SupportButton) => {
    setButtonsNotice(null);
    try {
      await api.adminSupportButtons.update({
        id: btn.id,
        label: btn.label,
        action: btn.action,
        payload: btn.payload ?? "",
        enabled: Boolean(btn.enabled),
        sort_order: Number(btn.sort_order) || 100,
      });
      setButtonsNotice("Сохранено");
      setTimeout(() => setButtonsNotice(null), 1800);
      await refreshButtons();
    } catch (e: any) {
      setButtonsNotice(e?.message || "Ошибка сохранения");
      setTimeout(() => setButtonsNotice(null), 2400);
    }
  };

  const removeButton = async (id: number) => {
    setButtonsNotice(null);
    try {
      await api.adminSupportButtons.remove(id);
      setButtons((prev) => prev.filter((b) => b.id !== id));
      setButtonsNotice("Удалено");
      setTimeout(() => setButtonsNotice(null), 1800);
    } catch (e: any) {
      setButtonsNotice(e?.message || "Ошибка удаления");
      setTimeout(() => setButtonsNotice(null), 2400);
    }
  };

  const createButton = async () => {
    if (!newBtn.label || !newBtn.action) {
      setButtonsNotice("Нужно название и действие");
      setTimeout(() => setButtonsNotice(null), 2400);
      return;
    }
    setButtonsNotice(null);
    try {
      await api.adminSupportButtons.create({
        label: newBtn.label,
        action: newBtn.action as SupportButton["action"],
        payload: newBtn.payload ?? "",
        enabled: Boolean(newBtn.enabled),
        sort_order: Number(newBtn.sort_order) || 100,
      });
      setNewBtn({ label: "", action: "link", payload: "", enabled: true, sort_order: 100 });
      await refreshButtons();
      setButtonsNotice("Добавлено");
      setTimeout(() => setButtonsNotice(null), 1800);
    } catch (e: any) {
      setButtonsNotice(e?.message || "Ошибка добавления");
      setTimeout(() => setButtonsNotice(null), 2400);
    }
  };

  return (
    <div className="grid gap-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => ROUTE.go(ROUTE.super())}
          className="ui-btn-sm"
        >
          ← Назад
        </button>
        <div className="text-[22px] font-bold tracking-tight">Настройки</div>
      </div>

      <div className="ui-card p-6">
        <div className="text-sm text-neutral-300 mb-2">Стартовое сообщение бота</div>
        <textarea
          value={startMsg}
          onChange={(e) => setStartMsg(e.target.value)}
          placeholder="Текст для /start"
          className="ui-input min-h-[160px]"
          disabled={loading}
        />
        <div className="mt-3 flex items-center gap-2">
          <button onClick={save} className="btn-primary" disabled={saving || loading}>
            {saving ? "Сохраняем…" : "Сохранить"}
          </button>
          {notice && <span className="text-xs text-white/70">{notice}</span>}
        </div>
      </div>

      <div className="ui-card p-6">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="text-sm text-neutral-300">Кнопки</div>
          <button type="button" className="ui-btn-sm" onClick={refreshButtons} disabled={buttonsLoading}>
            {buttonsLoading ? "Обновляем…" : "Обновить"}
          </button>
        </div>

        <div className="ui-panel mb-4">
          <div className="text-xs text-white/60 mb-2">Добавить кнопку</div>
          <div className="grid gap-2">
            <input
              className="ui-input"
              placeholder="Название кнопки"
              value={newBtn.label || ""}
              onChange={(e) => setNewBtn((s) => ({ ...s, label: e.target.value }))}
            />
            <select
              className="ui-input"
              value={newBtn.action || "link"}
              onChange={(e) => setNewBtn((s) => ({ ...s, action: e.target.value as SupportButton["action"] }))}
            >
              {ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
            <input
              className="ui-input"
              placeholder={payloadPlaceholder((newBtn.action as SupportButton["action"]) || "link")}
              value={newBtn.payload || ""}
              onChange={(e) => setNewBtn((s) => ({ ...s, payload: e.target.value }))}
            />
            <div className="flex items-center gap-3 text-sm text-white/70">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(newBtn.enabled)}
                  onChange={(e) => setNewBtn((s) => ({ ...s, enabled: e.target.checked }))}
                />
                Активна
              </label>
              <input
                className="ui-input w-28"
                type="number"
                placeholder="Порядок"
                value={newBtn.sort_order ?? 100}
                onChange={(e) => setNewBtn((s) => ({ ...s, sort_order: Number(e.target.value) }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <button className="btn-primary" onClick={createButton}>
                Добавить
              </button>
              {buttonsNotice && <span className="text-xs text-white/70">{buttonsNotice}</span>}
            </div>
          </div>
        </div>

        <div className="grid gap-3">
          {buttons.length === 0 && (
            <div className="text-xs text-white/60">
              Кнопок нет — добавьте первую выше.
            </div>
          )}
          {buttons.map((btn) => (
            <div key={btn.id} className="ui-panel">
              <div className="grid gap-2">
                <input
                  className="ui-input"
                  value={btn.label}
                  onChange={(e) => updateButton(btn.id, { label: e.target.value })}
                  placeholder="Название кнопки"
                />
                <select
                  className="ui-input"
                  value={btn.action}
                  onChange={(e) => updateButton(btn.id, { action: e.target.value as SupportButton["action"] })}
                >
                  {ACTIONS.map((a) => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
                <input
                  className="ui-input"
                  value={btn.payload || ""}
                  onChange={(e) => updateButton(btn.id, { payload: e.target.value })}
                  placeholder={payloadPlaceholder(btn.action)}
                />
                <div className="flex items-center gap-3 text-sm text-white/70">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(btn.enabled)}
                      onChange={(e) => updateButton(btn.id, { enabled: e.target.checked })}
                    />
                    Активна
                  </label>
                  <input
                    className="ui-input w-28"
                    type="number"
                    value={btn.sort_order ?? 100}
                    onChange={(e) => updateButton(btn.id, { sort_order: Number(e.target.value) })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button className="ui-btn-sm" onClick={() => saveButton(btn)}>
                    Сохранить
                  </button>
                  <button className="ui-btn-sm" onClick={() => removeButton(btn.id)}>
                    Удалить
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
